import { Box, Flex, IconButton, Input, SimpleGrid } from '@chakra-ui/react'
import {
  ArcElement,
  CategoryScale,
  Chart,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip
} from 'chart.js'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { EmptyCell } from '../../../../components/EmptyCell'
import { Icon } from '../../../../components/Icon'
import { LoadingBlock } from '../../../../components/Loading'
import { SectionCard, SectionTitle } from '../../../../components/SectionCard'
import { useHasRole, useAppToast } from '../../../../helpers'
import { IFinancialMetrics, IMonthlyData } from '../../../../interfaces/IFinancialIndicators'
import FinancialIndicatorsService from '../../../../services/FinancialIndicators/FinancialIndicatorsService'
import { magenta } from '../../../../theme'
import MetricCard from './MetricCard'
import PerformanceChart from './PerformanceChart'
import PlrSection from './PlrSection'

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend, ArcElement)

const service = new FinancialIndicatorsService()

const parseMonthInput = (value: string): { year: number; month: number } => {
  const [year, month] = value.split('-')
  return { year: parseInt(year, 10), month: parseInt(month, 10) }
}

const formatMonthInput = (year: number, month: number): string => {
  return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}`
}

export default function FinancialIndicatorsSection() {
  const toast = useAppToast()
  const hasRole = useHasRole()
  const canEdit = hasRole('edit_financial_indicators')
  const navigate = useNavigate()

  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth() + 1

  const [startMonth, setStartMonth] = useState(1)
  const [startYear, setStartYear] = useState(currentYear)
  const [endMonth, setEndMonth] = useState(currentMonth)
  const [endYear, setEndYear] = useState(currentYear)

  const [metrics, setMetrics] = useState<IFinancialMetrics | null>(null)
  const [previousMetrics, setPreviousMetrics] = useState<IFinancialMetrics | null>(null)
  const [monthlyData, setMonthlyData] = useState<IMonthlyData[]>([])
  const [previousYearData, setPreviousYearData] = useState<IMonthlyData[]>([])
  const [loading, setLoading] = useState(false)
  const [lastFilledYear, setLastFilledYear] = useState<number | null>(null)
  const [lastFilledMonth, setLastFilledMonth] = useState<number | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)

  const isDateBeforeOrEqual = (year: number, month: number) => {
    return year < currentYear || (year === currentYear && month <= currentMonth)
  }

  const isDateBeforeOrEqualToLastFilled = (year: number, month: number) => {
    if (!lastFilledYear || !lastFilledMonth) {
      return isDateBeforeOrEqual(year, month)
    }
    return year < lastFilledYear || (year === lastFilledYear && month <= lastFilledMonth)
  }

  const isStartBeforeEnd = (sYear: number, sMonth: number, eYear: number, eMonth: number) => {
    return sYear < eYear || (sYear === eYear && sMonth <= eMonth)
  }

  const handleStartMonthInput = (value: string) => {
    if (!value) return
    const { year, month } = parseMonthInput(value)
    if (!isDateBeforeOrEqualToLastFilled(year, month)) {
      toast.warning('Período não pode ser maior que o último mês preenchido.')
      return
    }
    if (!isStartBeforeEnd(year, month, endYear, endMonth)) {
      toast.warning('Data inicial não pode ser maior que data final.')
      return
    }
    setStartYear(year)
    setStartMonth(month)
  }

  const handleEndMonthInput = (value: string) => {
    if (!value) return
    const { year, month } = parseMonthInput(value)
    if (!isDateBeforeOrEqualToLastFilled(year, month)) {
      toast.warning('Período não pode ser maior que o último mês preenchido.')
      return
    }
    if (!isStartBeforeEnd(startYear, startMonth, year, month)) {
      toast.warning('Data final não pode ser menor que data inicial.')
      return
    }
    setEndYear(year)
    setEndMonth(month)
  }

  useEffect(() => {
    fetchData()
  }, [startMonth, startYear, endMonth, endYear])

  useEffect(() => {
    if (!isInitialized && lastFilledYear && lastFilledMonth) {
      setStartMonth(1)
      setStartYear(lastFilledYear)
      setEndYear(lastFilledYear)
      setEndMonth(lastFilledMonth)
      setIsInitialized(true)
    }
  }, [lastFilledYear, lastFilledMonth, isInitialized])

  async function fetchData() {
    setLoading(true)
    try {
      const res = await service.getIndicators(startYear, startMonth, endYear, endMonth)
      setMetrics(res.data.metrics)
      setPreviousMetrics(res.data.previous_year_metrics)
      setMonthlyData(res.data.monthly_data)
      setPreviousYearData(res.data.previous_year_monthly_data)
      setLastFilledYear(res.data.last_filled_year)
      setLastFilledMonth(res.data.last_filled_month)
    } catch {
      setMetrics(null)
      setPreviousMetrics(null)
      setMonthlyData([])
      setPreviousYearData([])
      toast.error('Erro ao carregar indicadores financeiros.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <SectionCard
      variant="flush"
      title="Resultados Financeiros"
      actions={
        <Flex align="center" gap={2}>
          <Input
            type="month"
            size="sm"
            w="180px"
            value={formatMonthInput(startYear, startMonth)}
            onChange={(e) => handleStartMonthInput(e.target.value)}
            min={formatMonthInput(2020, 1)}
            max={formatMonthInput(currentYear, currentMonth)}
          />
          <EmptyCell />
          <Input
            type="month"
            size="sm"
            w="180px"
            value={formatMonthInput(endYear, endMonth)}
            onChange={(e) => handleEndMonthInput(e.target.value)}
            min={formatMonthInput(2020, 1)}
            max={
              lastFilledYear && lastFilledMonth
                ? formatMonthInput(lastFilledYear, lastFilledMonth)
                : formatMonthInput(currentYear, currentMonth)
            }
          />
          {canEdit && (
            <IconButton
              icon={<Icon name="lni lni-cog" size="lg" />}
              variant="ghost"
              size="sm"
              aria-label="Configurações de indicadores financeiros"
              onClick={() => navigate('/financial-indicators')}
            />
          )}
        </Flex>
      }
    >
      <Box p={4}>
        <SectionTitle as="h3">Métricas</SectionTitle>

        {loading ? (
          <LoadingBlock mb={6} />
        ) : metrics ? (
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={4} mb={6}>
            <MetricCard
              icon="lni lni-stats-up"
              title="Faturamento"
              indicator="R$"
              value={metrics.revenue}
              previousValue={previousMetrics?.revenue ?? null}
              color={magenta[500]}
              sparklineData={monthlyData.map((d) => d.revenue)}
            />
            <MetricCard
              icon="lni lni-stats-down"
              title="Despesas"
              indicator="R$"
              value={metrics.expenses}
              previousValue={previousMetrics?.expenses ?? null}
              color="#5470DE"
              sparklineData={monthlyData.map((d) => d.expenses)}
            />
            <MetricCard
              icon="lni lni-coin"
              title="Lucro"
              indicator="R$"
              value={metrics.profit}
              previousValue={previousMetrics?.profit ?? null}
              color="#07BEAA"
              sparklineData={monthlyData.map((d) => d.profit)}
            />
            <MetricCard
              icon="lni lni-pie-chart"
              title="Margem de Lucro"
              indicator="%"
              value={metrics.profit_margin}
              previousValue={previousMetrics?.profit_margin ?? null}
              color="#1786C4"
              sparklineData={monthlyData.map((d) => d.profit_margin)}
            />
          </SimpleGrid>
        ) : null}

        {/* Seção Performance */}
        <Box mb={6}>
          <PerformanceChart monthlyData={monthlyData} previousYearData={previousYearData} loading={loading} />
        </Box>

        {/* Seção PLR */}
        <PlrSection onError={(msg) => toast.error(msg)} />
      </Box>
    </SectionCard>
  )
}
